import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowLeft, ArrowRight, Loader2, MailCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * ForgotPasswordPage
 *
 * Public route. User enters their email; we call Supabase
 * `resetPasswordForEmail` which mails them a magic link that lands on
 * `/reset-password` with a temporary recovery session.
 *
 * We intentionally always show the SAME success state regardless of whether
 * the email exists — this prevents account-enumeration.
 */
const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const { resetPasswordForEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim();
    if (!trimmed) {
      setError(t('auth.forgot_email_required'));
      return;
    }
    setSubmitting(true);
    const { error: resetError } = await resetPasswordForEmail(trimmed);
    setSubmitting(false);
    // Show success state either way — account enumeration mitigation.
    // We only surface hard system errors (e.g. network) to the user.
    if (resetError && /network|fetch|timeout/i.test(resetError.message || '')) {
      setError(resetError.message);
      return;
    }
    setSent(true);
  };

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden bg-background text-foreground"
      data-testid="forgot-password-page"
    >
      {/* Backdrop matches LoginPage */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(120% 80% at 50% 0%, hsl(var(--primary) / 0.10) 0%, hsl(var(--background)) 60%)',
        }}
      />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="mb-6 flex flex-col items-center text-center space-y-3">
            <img
              src="/kocore-logo.png"
              alt="K.O. CO+RE Systems"
              className="h-28 w-auto"
              data-testid="forgot-brand-logo"
            />
            <h1 className="text-xl font-semibold tracking-tight">
              {t('auth.forgot_title')}
            </h1>
            <p className="text-sm text-muted-foreground max-w-xs">
              {sent ? t('auth.forgot_sent_subtitle') : t('auth.forgot_subtitle')}
            </p>
          </div>

          <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] shadow-2xl shadow-black/40">
            <CardContent className="p-6 sm:p-8">
              {sent ? (
                <div
                  className="flex flex-col items-center text-center gap-4"
                  data-testid="forgot-password-sent"
                >
                  <div className="h-12 w-12 rounded-full bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center">
                    <MailCheck className="h-6 w-6 text-[hsl(var(--primary))]" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('auth.forgot_sent_body')}
                  </p>
                  <Link
                    to="/login"
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))] hover:underline underline-offset-4"
                    data-testid="forgot-back-to-login-link"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t('auth.back_to_login')}
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <div className="space-y-2">
                    <Label
                      htmlFor="forgot-email"
                      className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
                    >
                      {t('auth.email')}
                    </Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      autoComplete="email"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      aria-invalid={!!error}
                      className="h-11 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 hover:border-[hsl(var(--primary))]/30 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 focus-visible:border-[hsl(var(--primary))]/40 transition-colors"
                      data-testid="forgot-email-input"
                    />
                  </div>

                  {error ? (
                    <Alert
                      variant="default"
                      className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
                      data-testid="forgot-error-message"
                    >
                      <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
                      <AlertDescription className="text-sm">{error}</AlertDescription>
                    </Alert>
                  ) : null}

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="group relative w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/85 active:scale-[0.99] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 transition-colors font-semibold tracking-[0.16em] uppercase"
                    data-testid="forgot-submit-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('auth.forgot_sending')}
                      </>
                    ) : (
                      <>
                        {t('auth.forgot_send_link')}
                        <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </Button>

                  <div className="text-center">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
                      data-testid="forgot-back-to-login-link"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      {t('auth.back_to_login')}
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-[11px] uppercase tracking-[0.28em] text-muted-foreground/70">
            © K.O. CO+RE SYSTEMS · Secure Terminal
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
